import React, { useEffect } from 'react';
import { observer } from 'src/utils/mobx-react';
import { BlockingLicenceNotification } from './BlockingLicenceNotification';
import { BlockingNotificationType, NotificationType } from 'src_server/trpc/types/accountModel/accountResponse';
import { BlockingTermsNotification } from './BlockingTermsNotification';
import { BlockingContactPreferencesNotification } from './BlockingContactPreferencesNotification';
import { PrivacyPolicyNotification } from './PrivacyPolicyNotification';
import { BlockingKycDepositRestrictionNotification } from './BlockingKycDepositRestrictionNotification';
import { BlockingRingedfencedFundsNotification } from './BlockingRingfencedFundsNotification';
import { useCommon } from 'src/domains/common/Common';
import { BlockingRTPNotification } from './BlockingRTPNotification';
import { BlockingNotificationsState } from 'src/domains/players/state/BlockingNotification.state';
import { CmsMarkersOfHarmNotification } from './CmsMarkersOfHarmNotification';
import { BlockingGamstopPreviouslyPositiveNotification } from './BlockingGamstopPreviouslyPositiveNotification';
import { BlockingDepositLimitCalendarNotification } from './BlockingDepositLimitCalendarNotification';
import { BlockingDepositLimitCalendarPreviewNotification } from './BlockingDepositLimitCalendarPreviewNotification';

interface BlockingNotificationsPropsType {
    blockingNotificationType: NotificationType;
}

export const BlockingNotifications = observer(
    'BlockingNotifications',
    ({ blockingNotificationType }: BlockingNotificationsPropsType): JSX.Element | null => {
        const common = useCommon();
        const blockingNotificationsState = BlockingNotificationsState.get(common);
        useEffect(() => {
            void blockingNotificationsState.markNotificationShown(blockingNotificationType);
        }, [blockingNotificationType, blockingNotificationsState]);

        switch (blockingNotificationType) {
            case BlockingNotificationType.CHANGE_OF_LICENSE:
                return (
                    <BlockingLicenceNotification
                        onConfirm={blockingNotificationsState.handleConfirmNewLicenceNotification}
                    />
                );
            case BlockingNotificationType.PRIVACY_UPDATE:
                return (
                    <PrivacyPolicyNotification onConfirm={blockingNotificationsState.handleConfirmNewPrivacyPolicy} />
                );
            case BlockingNotificationType.TERMS_UPDATE:
                return <BlockingTermsNotification onConfirm={blockingNotificationsState.handleConfirmNewTerms} />;
            case BlockingNotificationType.KYC_DEPOSIT_RESTRICTION:
                //for operators with idMission: false
                return (
                    <BlockingKycDepositRestrictionNotification
                        onConfirm={blockingNotificationsState.handleConfirmKycDepositRestrictionNotification}
                    />
                );
            case BlockingNotificationType.CONTACT_PREFERENCES:
                return (
                    <BlockingContactPreferencesNotification
                        onConfirm={blockingNotificationsState.handleConfirmContactPreferences}
                    />
                );
            case BlockingNotificationType.RINGFENCED_FUNDS_UPDATE:
                return (
                    <BlockingRingedfencedFundsNotification
                        onConfirm={blockingNotificationsState.handleConfirmRingfencedfunds}
                    />
                );
            case BlockingNotificationType.RTP_UPDATE:
                return <BlockingRTPNotification onConfirm={blockingNotificationsState.handleConfirmRTPNotification} />;
            case BlockingNotificationType.GAMSTOP_PREVIOUSLY_POSITIVE:
                return (
                    <BlockingGamstopPreviouslyPositiveNotification
                        onConfirm={blockingNotificationsState.handleConfirmGamstopPreviouslyPositive}
                    />
                );
            case BlockingNotificationType.DEPOSIT_LIMIT_CALENDAR_UPDATE:
                return (
                    <BlockingDepositLimitCalendarNotification
                        onConfirm={blockingNotificationsState.handleConfirmDepositLimitCalendarUpdate}
                    />
                );
            case BlockingNotificationType.DEPOSIT_LIMIT_CALENDAR_UPDATE_PREVIEW:
                return (
                    <BlockingDepositLimitCalendarPreviewNotification
                        onConfirm={blockingNotificationsState.handleConfirmDepositLimitCalendarUpdatePreview}
                    />
                );
            default: {
                const popup = blockingNotificationsState.getActiveCmsMarkersOfHarmPopup;
                if (popup === null) {
                    return null;
                }

                return (
                    <CmsMarkersOfHarmNotification
                        popup={popup}
                        onConfirm={(options) =>
                            blockingNotificationsState.handleConfirmCmsNotification(blockingNotificationType, options)
                        }
                    />
                );
            }
        }
    }
);
